import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment-timezone';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { ToastContext, WeezHeader, Button, Section, Card, Form, Select, DateTimeInput } from '@weezevent/nacre';
import SubscriptionModel from '../../models/subscription';
import Loading from '../../components/loading';
import { useForm } from '../../utils/validators';
import { DISTRIBUTORS, WEBSERVICE_FORMS, getInitialValues, getTranslatedCreateFormByOptions, getValidators } from './webserviceUtils';
import Styles from './create/createWebservice.module.css';

export default function EditWebservice({ subscriptionId, organization, history }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const timezone = organization.timezone;
    const locale = i18n.language;
    const currentYear = new Date().getFullYear();

    const [isLoading, setIsLoading] = React.useState(true);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [selectedType, setSelectedType] = React.useState(null);

    const initialFormState = React.useMemo(() => getInitialValues(selectedType), [selectedType]);
    const validators = React.useMemo(() => getValidators(selectedType), [selectedType]);
    const [formState, setFormState, setFormStateObject, formErrors, formValidate] = useForm(initialFormState, validators);

    React.useEffect(() => {
        setIsLoading(true);
        SubscriptionModel.get({ id: subscriptionId, organizationId: organization.id })
            .then(subscription => {
                const type = subscription.type || '';
                setSelectedType(type);

                const initialValues = {
                    ...getInitialValues(type),
                    ...subscription.object,
                    type,
                    create_form_by: subscription.create_form_by || 'rate'
                };

                setFormStateObject(initialValues, true);
                setIsLoading(false);
            })
            .catch(() => {
                toast.error(t('ticket.webservice.sucbscriptionLinks.retrievalError'));
                setIsLoading(false);
            });
    }, [subscriptionId, organization, setFormStateObject]);

    const handleSubmit = React.useCallback(() => {
        const currentValidators = getValidators(formState.type);
        const formIsValid = formValidate(currentValidators);

        if (!formIsValid) {
            return;
        }

        setIsSubmitting(true);
        const subscription = new SubscriptionModel({ id: subscriptionId });
        subscription.type = formState.type;
        subscription.create_form_by = formState.create_form_by;

        if (!subscription.is_push()) {
            subscription.setInfo({ ...formState });
        } else {
            subscription.end_date = formState.end_date;
        }

        subscription
            .save({ organizationId: organization.id })
            .then(() => {
                toast.success(t('ticket.webservice.modificationSuccess'));
                history.push(`/ticket/O${organization.id}/webservices`);
            })
            .catch(error => {
                const message = t([`ticket.webservice.statuses.${error.code}`, 'ticket.webservice.creationError']);
                toast.error(message);
            })
            .finally(() => setIsSubmitting(false));
    }, [formState, formValidate, organization, toast, t, history, setIsSubmitting, subscriptionId]);

    // form error if selected date is in the past
    const warningPastDate = React.useMemo(() => {
        const now = moment()
            .tz(timezone)
            .format();
        if (formState.end_date && formState.end_date < now) {
            return t('ticket.webservice.create.import.date-error');
        }
        return false;
    }, [timezone, formState.end_date, t]);

    const renderFields = React.useCallback(() => {
        if (!formState.type) return null;
        const Component = WEBSERVICE_FORMS[formState.type];
        if (!Component) return null;
        return (
            <Section>
                <Component formState={formState} setFormState={setFormState} formErrors={formErrors} />
            </Section>
        );
    }, [formState, formErrors, setFormState]);

    const distributorOptions = [{ text: t('ticket.webservice.form.choiceDistributor'), value: '' }, ...DISTRIBUTORS];
    const isFormValid = Boolean(Object.keys(formErrors).length === 0 && formState.type);

    if (isLoading) {
        return <Loading />;
    }

    return (
        <React.Fragment>
            <WeezHeader
                title={t('ticket.webservice.editWebServices')}
                backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/webservices`) }}
                rightComponent={[<Button primary key="primary" label={t('ticket.common.action.save')} disabled={isSubmitting || !isFormValid} onClick={handleSubmit} />]}
            />
            <Card>
                <Card.Header>
                    <Card.Title title={`${t('ticket.webservice.distributor')} :`} />
                </Card.Header>
                <Card.Content>
                    <Form onSubmit={handleSubmit} loading={isSubmitting}>
                        <Form.Field>
                            {formState.type !== 'hook' ? (
                                <>
                                    <div style={{ marginBottom: '5px' }}>{t('ticket.webservice.form.choiceDistributorTitle')}</div>
                                    <Select
                                        value={formState.type}
                                        options={distributorOptions}
                                        onChange={(_, { value }) => {
                                            setSelectedType(value);
                                            setFormStateObject(
                                                {
                                                    ...getInitialValues(value),
                                                    type: value,
                                                    create_form_by: formState.create_form_by
                                                },
                                                true
                                            );
                                        }}
                                        disabled
                                    />
                                </>
                            ) : (
                                <div className={Styles['modal-settings-event-section']}>
                                    <div className={Styles['date-container']}>
                                        <DateTimeInput
                                            className={Styles['date-time-input']}
                                            timeLabel={
                                                <>
                                                    {t('ticket.common.time.end')}
                                                    <span className={Styles['optional']}>{t('ticket.common.labelOptional')}</span>
                                                </>
                                            }
                                            dateLabel={
                                                <>
                                                    {t('ticket.common.date.end')}
                                                    <span className={Styles['optional']}>{t('ticket.common.labelOptional')}</span>
                                                </>
                                            }
                                            onChange={date => setFormState('end_date', date)}
                                            value={formState.end_date}
                                            dateProps={{ minYear: currentYear, minDate: moment() }}
                                            locale={locale}
                                            timezone={timezone}
                                        />
                                        <div className={Styles['timezone-container']}>
                                            <div>{timezone}</div>
                                        </div>
                                    </div>
                                    {Boolean(warningPastDate) && <Form.FieldErrors errors={[warningPastDate]} />}
                                </div>
                            )}
                            <div style={{ marginBottom: '5px', marginTop: '15px' }}>{t('ticket.webservice.form.createFormBy.label')}</div>
                            <Select
                                value={formState.create_form_by}
                                options={getTranslatedCreateFormByOptions(t)}
                                onChange={(_, { value }) => setFormState('create_form_by', value)}
                            />
                        </Form.Field>
                        {renderFields()}
                    </Form>
                </Card.Content>
            </Card>
        </React.Fragment>
    );
}

EditWebservice.propTypes = {
    subscriptionId: PropTypes.string.isRequired,
    organization: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired
};
